import { observer } from 'mobx-react-lite'
import React from 'react'
import { formatBytesToNumberWithType } from 'shared/lib'
import { Alert, Box, IconButton, Label, Typography, UploadDoc } from 'shared/ui'
import { AttachmentCard, AttachmentPdf } from 'entities/Attachment'
import { type IRSEINDocumentUploaderStore } from './store/IRSEINDocumentUploaderStore'
import styles from './styles.module.scss'

type IIRSEINDocumentUploaderProps = {
  store: IRSEINDocumentUploaderStore
}

export const IRSEINDocumentUploader = observer(({ store }: IIRSEINDocumentUploaderProps) => {
  const {
    attachmentError,
    clearAttachmentError,
    uploadIrsEinAttachment,
    attachmentData,
    deleteIrsEinAttachment,
  } = store

  return (
    <>
      {attachmentError && (
        <Alert
          item={{ title: attachmentError.title, desc: attachmentError.desc, type: 'error' }}
          onClose={clearAttachmentError}
          marginBottom={16}
        />
      )}
      <Label
        marginBottom={8}
        label={{
          text: 'IRS EIN document',
          secondaryText: '(optional)',
          rightText: <div className={styles.recommendedLabel}>Recommended</div>,
        }}
      />
      <UploadDoc
        uploadTypeText='Acceptable file types: pdf, jpg, png'
        onChange={(files) => uploadIrsEinAttachment(files[0])}
      />
      <Typography
        variant='text-input-helper'
        ariaLabel='textInputHelper'
        color='var(--content-primary-tertiary)'
        tag='div'
        marginTop={'8px'}
      >
        Upload your company&apos;s IRS EIN document to increase your chances of getting approved
      </Typography>

      {attachmentData && (
        <Box display='flex' justifyContent={'space-between'} alignItems={'center'} marginTop={4}>
          <Box display='flex' gap={'12px'} alignItems={'center'}>
            {attachmentData.type === 'attachment' ? (
              <AttachmentPdf loading={attachmentData.loading} />
            ) : (
              <AttachmentCard attachment={attachmentData} loading={attachmentData.loading} />
            )}

            <Box display={'flex'} flexDirection={'column'} gap={1} justifyContent={'center'}>
              <Typography
                ariaLabel='attachmentName'
                variant='body-md-regular'
                color='var(--content-primary-primary)'
              >
                {attachmentData.name}
              </Typography>
              <Typography
                ariaLabel='attachmentName'
                variant='body-sm-regular'
                color='var(--content-primary-tertiary)'
              >
                {formatBytesToNumberWithType(attachmentData.size || 0)}
              </Typography>
            </Box>
          </Box>
          <IconButton
            icon='close'
            ariaLabel='delete_document'
            size='small'
            variant='icon'
            color='tertiary'
            transparentBackground={true}
            onClick={deleteIrsEinAttachment}
          />
        </Box>
      )}
    </>
  )
})
